<template>
    <Card class="marketing-preferences-attributes__container">
        <h1 class="marketing-preferences-attributes__title typographies-heading-h1">
            {{ $t.__('marketing.preferences.attributes.MarketingPreferencesAttributes.title') }}
        </h1>

        <p
            class="marketing-preferences-attributes__description typographies-paragraph-medium-regular"
        >
            {{
                $t.__('marketing.preferences.attributes.MarketingPreferencesAttributes.description')
            }}
        </p>

        <MarketingPreferencesAttributesTable
            :attributes="synchronizableAttributes"
            :categories="categories"
            :isLoading="isLoading"
            @update:isLoading="
                (newValue) => {
                    isLoading = newValue;
                }
            "
        />

        <div class="marketing-preferences-attributes__footer">
            <CompassTooltip
                :disabled="!synchronizationInProgress"
                :content="
                    $t.__(
                        'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronization_already_start',
                    )
                "
                placement="top-end"
            >
                <CompassButton
                    :label="$t.__('common.actions.synchronise')"
                    :disabled="synchronizationInProgress || !hasChangesToAttributes"
                    @click="openAttributesRevisionDiffModal"
                />
            </CompassTooltip>
        </div>

        <CompassDialog
            :title="
                $t.__(
                    'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronization_details',
                )
            "
            :visible.sync="displayAttributesRevisionDiffModal"
            size="large"
        >
            <template #default>
                <div class="marketing-attributes-revision-diff-modal__content">
                    <CompassTabs
                        v-model="selectedModalTab"
                        class="marketing-attributes-revision-diff-modal__tabs"
                    >
                        <CompassTabPane
                            v-if="hasCreatedAttributes"
                            :label="
                                $t.__(
                                    'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronized_fields',
                                )
                            "
                            name="create"
                        >
                            <MarketingPreferencesAttributesRevisionTab
                                :attributes="attributesRevisionDiff.create"
                            />
                        </CompassTabPane>

                        <CompassTabPane
                            v-if="hasDeletedAttributes"
                            :label="
                                $t.__(
                                    'marketing.preferences.attributes.MarketingPreferencesAttributes.desynchronized_fields',
                                )
                            "
                            name="delete"
                        >
                            <MarketingPreferencesAttributesRevisionTab
                                :attributes="attributesRevisionDiff.delete"
                            />
                        </CompassTabPane>
                    </CompassTabs>

                    <CompassAlert
                        v-if="hasDeletedAttributes"
                        :description="
                            $t.__(
                                'marketing.preferences.attributes.MarketingPreferencesAttributes.careful_about_desynchronize_fields',
                            )
                        "
                        :show-close="false"
                        type="warning"
                    />
                </div>
            </template>

            <template #footer>
                <div class="marketing-attributes-revision-diff-modal__footer">
                    <div class="marketing-attributes-revision-diff-modal__footer-buttons">
                        <CompassButton
                            :label="
                                $t.__(
                                    'marketing.preferences.attributes.MarketingPreferencesAttributes.back_to_selection',
                                )
                            "
                            type="secondary"
                            @click="displayAttributesRevisionDiffModal = false"
                        />

                        <CompassButton
                            :label="
                                $t.__(
                                    'marketing.preferences.attributes.MarketingPreferencesAttributes.start_synchronization',
                                )
                            "
                            :loading="isLoading"
                            :disabled="!hasCreatedAttributes && !hasDeletedAttributes"
                            @click="synchronizeAttributes"
                        />
                    </div>
                </div>
            </template>
        </CompassDialog>
    </Card>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import marketingService from 'vueDir/api/marketing/marketingService.js';
import MarketingPreferencesAttributesTable from 'vcomponents/marketing/preferences/attributes/MarketingPreferencesAttributesTable.vue';
import MarketingPreferencesAttributesRevisionTab from 'vcomponents/marketing/preferences/attributes/revision/MarketingPreferencesAttributesRevisionTab.vue';
import Card from 'vcomponents/common/card/Card.vue';

export default {
    components: {
        Card,
        Preloader,
        MarketingPreferencesAttributesTable,
        MarketingPreferencesAttributesRevisionTab,
    },
    data() {
        return {
            isLoading: false,
            initialAttributes: [],
            synchronizableAttributes: [],
            categories: [],
            synchronizationInProgress: false,
            attributesRevisionDiff: {
                create: [],
                delete: [],
                void: [],
            },
            displayAttributesRevisionDiffModal: false,
            selectedModalTab: '',
            getAttributesRevisionDiffURL: '',
            synchronizeAttributesURL: '',
        };
    },
    computed: {
        hasChangesToAttributes() {
            let hasChanges = false;
            for (const initialAttribute of this.initialAttributes) {
                if (
                    this.synchronizableAttributes.some(
                        (synchronizableAttribute) =>
                            synchronizableAttribute.id === initialAttribute.id &&
                            synchronizableAttribute.synchronized !== initialAttribute.synchronized,
                    )
                ) {
                    hasChanges = true;
                    break;
                }
            }

            return hasChanges;
        },
        synchronizedAttributes() {
            return this.synchronizableAttributes.filter(
                (synchronizableAttribute) =>
                    synchronizableAttribute.synchronized || synchronizableAttribute.default,
            );
        },
        synchronizedAttributesId() {
            return this.synchronizedAttributes.map(
                (synchronizedAttribute) => synchronizedAttribute.id,
            );
        },
        hasCreatedAttributes() {
            return this.attributesRevisionDiff.create.length > 0;
        },
        hasDeletedAttributes() {
            return this.attributesRevisionDiff.delete.length > 0;
        },
    },
    created() {
        if (this.synchronizationInProgress) {
            this.pushSyncrhonizationInProgressNotif();
        }

        this.getSynchronizableAttributes();
    },
    methods: {
        getSynchronizableAttributes() {
            this.isLoading = true;
            marketingService
                .getSynchronizableAttributes()
                .then((response) => {
                    this.getAttributesRevisionDiffURL = response.links[0].href.replace('/v2', '');
                    this.initialAttributes = response.attributes;
                    this.synchronizableAttributes = [];
                    this.initialAttributes.sort((a, b) => {
                        if (a.category.code === b.category.code) {
                            return a.label.toLowerCase() > b.label.toLowerCase()
                                ? 1
                                : b.label.toLowerCase() > a.label.toLowerCase()
                                ? -1
                                : 0;
                        } else {
                            return a.category.code > b.category.code
                                ? 1
                                : b.category.code > a.category.code
                                ? -1
                                : 0;
                        }
                    });

                    for (const attribute of this.initialAttributes) {
                        if (
                            !this.categories.some(
                                (category) => category.id === attribute.category.code,
                            )
                        ) {
                            this.synchronizableAttributes.push({
                                ...attribute.category,
                                subheading: 'cat',
                            });

                            this.categories.push({
                                id: attribute.category.code,
                                label: attribute.category.label,
                            });
                        }

                        this.synchronizableAttributes.push(
                            _.cloneDeep({ ...attribute, hide: false }),
                        );
                    }
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('common.labels.error_retry'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        openAttributesRevisionDiffModal() {
            if (this.getAttributesRevisionDiffURL !== '') {
                this.isLoading = true;
                this.attributesRevisionDiff = {
                    create: [],
                    delete: [],
                    void: [],
                };

                marketingService
                    .getAttributesRevisionDiff(
                        this.getAttributesRevisionDiffURL,
                        this.synchronizedAttributesId,
                    )
                    .then((response) => {
                        this.synchronizeAttributesURL = response.links[0].href.replace('/v2', '');
                        this.attributesRevisionDiff = {
                            create: response.create,
                            delete: response.delete,
                            void: response.void,
                        };

                        if (this.hasCreatedAttributes) {
                            this.selectedModalTab = 'create';
                        } else if (this.hasDeletedAttributes) {
                            this.selectedModalTab = 'delete';
                        }

                        this.displayAttributesRevisionDiffModal = true;
                    })
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: this.$t.__('common.labels.error_retry'),
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
        synchronizeAttributes() {
            if (this.synchronizeAttributesURL !== '') {
                this.isLoading = true;
                marketingService
                    .synchronizeAttributes(
                        this.synchronizeAttributesURL,
                        this.synchronizedAttributesId,
                    )
                    .then(() => {
                        this.displayAttributesRevisionDiffModal = false;
                        this.synchronizationInProgress = true;
                        this.pushSyncrhonizationInProgressNotif();
                    })
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: this.$t.__('common.labels.error_retry'),
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
        pushSyncrhonizationInProgressNotif() {
            this.$pushNotification({
                text: `<span class="typographies-heading-h4">${this.$t.__(
                    'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronization_in_progress',
                )}</span> <span class="typographies-paragraph-medium-regular">${this.$t.__(
                    'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronization_in_progress_description',
                )}</span>`,
                isSellsy: true,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.marketing-preferences-attributes {
    &__container {
        max-width: 1104px;
        margin-bottom: var(--spacing-7);
        ::v-deep {
            .preloader {
                position: fixed;
                height: 100vh;
            }
        }
    }

    &__title {
        margin-bottom: var(--spacing-4);
    }

    &__description {
        margin-top: 0;
        margin-bottom: var(--spacing-4);
    }

    &__tabs {
        ::v-deep {
            .compass-tabs__headers {
                margin-bottom: var(--spacing-3);
            }
        }
    }

    &__footer {
        position: fixed;
        right: 0;
        bottom: 0;
        left: 64px;
        z-index: 5;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--spacing-1);
        padding: var(--spacing-3) var(--spacing-5);
        box-shadow: 0px 0px 12px 0px rgba(17, 17, 17, 0.24);
        background-color: var(--colors-common-background-default);
    }
}

.marketing-attributes-revision-diff-modal {
    &__tabs {
        margin-bottom: var(--spacing-3);

        ::v-deep {
            .compass-tabs__headers {
                margin-bottom: var(--spacing-3);
            }
        }
    }

    &__footer {
        padding: var(--spacing-5);

        &-buttons {
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
            gap: var(--spacing-3);
        }
    }
}
</style>
